import { yupResolver } from "@hookform/resolvers/yup";
import { LOGIN } from "config/routes";
import { generateVanillaAuthUrl, setAuthCookies } from "helpers/auth";
import React, { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import BcoHeader from "shared/components/BcoHeader";
import { FlexLayout, GridColumnLayout, Text } from "shared/globalStyles";
import { useCheckMobile } from "shared/hooks/useCheckMobile";
import { registrationViewTracking } from "shared/utils/analytics";
import AboutCommunityItems from "./components/AboutCommunityItems";

import SignUpForm from "./components/SignUpForm";
import signupSchema from "./schemas/signupSchema";
import { IUserSignUpInfo, cognitoSignup } from "./services";
import { SingUpWrapper } from "./styles";
import { getLangFromQuery } from "helpers/getLangFromQuery";

const SignUp: React.FC = () => {
  const { t, i18n } = useTranslation("global");
  const { language } = i18n;
  const navigate = useNavigate();
  const isMobile = useCheckMobile();
  const { handleSubmit, control, formState } = useForm({
    resolver: yupResolver(signupSchema),
    mode: "onChange",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [responseError, setResponseError] = useState<string>("");
  const { errors } = formState;
  const fontSizeTitle = isMobile ? "24px" : "36px";

  const search = window.location.search;
  const locale = getLangFromQuery(search);

  const onSubmit = async (data: FieldValues) => {
    try {
      setIsLoading(true);

      const { emailAddress, password, username, getEmail, consentPrivacyPolicyLegalAgeChecks } =
        data;

      const userInformation = {
        email: emailAddress,
        password: password,
        emailOptIn: getEmail || false,
        consent: !!consentPrivacyPolicyLegalAgeChecks,
        username: username,
        locale: language,
        skipVerificationEmail: true,
      } as IUserSignUpInfo;

      const res = await cognitoSignup(userInformation);

      // bco-ui needs the auth cookies to log in the user
      setAuthCookies(res.data.accessToken, res.data.refreshToken, res.data.idToken);

      const redirectionToVanilla = await generateVanillaAuthUrl(
        res.data.idToken,
        `${process.env.REACT_APP_VANILLA_JWT_URL}/${language}`,
        language,
      );

      setIsLoading(false);
      window.location.replace(redirectionToVanilla);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.warn(error?.response?.data?.message, "Error in onSubmit");
      setResponseError(error?.response?.data?.message ?? "Unknown error");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    i18n.changeLanguage(locale);
  }, [locale, i18n]);

  useEffect(() => {
    setTimeout(() => {
      registrationViewTracking();
    }, 2000);
  }, []);

  return (
    <SingUpWrapper>
      <BcoHeader
        rightText={"signUp.login"}
        onClick={() =>
          navigate({
            pathname: LOGIN,
            search: getLangFromQuery(search),
          })
        }
      />
      <GridColumnLayout padding="36px 0 0 0" columnGap="10%">
        <SignUpForm
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          errors={errors}
          control={control}
          isLoading={isLoading}
          responseError={responseError}
        />
        <FlexLayout flexDirection="column">
          <Text fontsize={fontSizeTitle}>{t("signUp.loginTitle")}</Text>
          <AboutCommunityItems />
        </FlexLayout>
      </GridColumnLayout>
    </SingUpWrapper>
  );
};

export default SignUp;
